// Common timezones organized by region
export const timezones = [
  // Americas
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Phoenix',
  'America/Toronto',
  'America/Vancouver',
  'America/Halifax',
  'America/St_Johns',
  'America/Mexico_City',
  'America/Cancun',
  'America/Bogota',
  'America/Lima',
  'America/Santiago',
  'America/Sao_Paulo',
  'America/Buenos_Aires',
  'America/Caracas',

  // Europe
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Brussels',
  'Europe/Vienna',
  'Europe/Zurich',
  'Europe/Prague',
  'Europe/Warsaw',
  'Europe/Budapest',
  'Europe/Athens',
  'Europe/Istanbul',
  'Europe/Moscow',
  'Europe/Dublin',
  'Europe/Lisbon',
  'Europe/Stockholm',
  'Europe/Oslo',
  'Europe/Copenhagen',
  'Europe/Helsinki',

  // Asia
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Dhaka',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Asia/Manila',
  'Asia/Jakarta',
  'Asia/Kuala_Lumpur',
  'Asia/Taipei',
  'Asia/Jerusalem',
  'Asia/Baghdad',
  'Asia/Riyadh',

  // Pacific
  'Pacific/Auckland',
  'Pacific/Fiji',
  'Pacific/Honolulu',
  'Pacific/Tahiti',
  'Pacific/Guam',

  // Australia
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Perth',
  'Australia/Adelaide',
  'Australia/Darwin',

  // Africa
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Africa/Lagos',
  'Africa/Nairobi',
  'Africa/Casablanca',
  'Africa/Algiers',

  // Atlantic
  'Atlantic/Reykjavik',
  'Atlantic/Azores',
  'Atlantic/Cape_Verde',

  // UTC
  'UTC'
];

// ICAO airport code to IANA timezone mapping
const AIRPORT_TIMEZONES = {
  'LPPR': 'Europe/Lisbon',
  'EIDW': 'Europe/Dublin',
  'EINN': 'Europe/Dublin',
};

/**
 * Get the IANA timezone for an airport ICAO code. Falls back to UTC.
 */
export function getAirportTimezone(icao) {
  return AIRPORT_TIMEZONES[icao] || 'UTC';
}

/**
 * Get the short timezone abbreviation (e.g. "WET", "IST", "GMT") for an airport at a given time.
 */
export function getTimezoneAbbr(airportIcao, date) {
  const tz = getAirportTimezone(airportIcao);
  try {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone: tz, timeZoneName: 'short' }).formatToParts(date || new Date());
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    return tzPart ? tzPart.value : '';
  } catch {
    return '';
  }
}

/**
 * Format an ETA time string (ISO format without Z) for display in the airport's local timezone.
 * Returns e.g. "15:55 WET"
 */
export function formatEtaTime(etaTime, airportIcao) {
  if (!etaTime) return '-';
  const tz = getAirportTimezone(airportIcao);
  const d = new Date(etaTime + 'Z');
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: tz });
  const abbr = getTimezoneAbbr(airportIcao, d);
  return abbr ? `${time} ${abbr}` : time;
}

/**
 * Clock-only variant of formatEtaTime: the arrival time in the airport's local
 * timezone with NO timezone abbreviation. Arrival times are always shown in
 * local time, so the "GMT+1" suffix was pure noise on the Live table.
 * Returns e.g. "10:41".
 */
export function formatEtaClock(etaTime, airportIcao) {
  if (!etaTime) return '-';
  const tz = getAirportTimezone(airportIcao);
  const d = new Date(etaTime + 'Z');
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: tz });
}

/**
 * Minutes between a predicted arrival and the scheduled arrival.
 * Negative = early, positive = late. Returns null when either side is missing.
 * Both timestamps are server-side UTC without a zone suffix.
 */
export function arrivalDeltaMinutes(etaTime, scheduledArrival) {
  if (!etaTime || !scheduledArrival) return null;
  const eta = new Date(etaTime + 'Z').getTime();
  const sched = new Date(scheduledArrival + 'Z').getTime();
  if (!Number.isFinite(eta) || !Number.isFinite(sched)) return null;
  return Math.round((eta - sched) / 60000);
}

/**
 * Format a server timestamp (ISO format without Z, UTC) for display in the airport's local timezone.
 * Returns e.g. "14:32 WET"
 */
export function formatServerTime(serverTime, airportIcao) {
  if (!serverTime) return '-';
  const tz = getAirportTimezone(airportIcao);
  const d = new Date(serverTime + 'Z');
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: tz });
  const abbr = getTimezoneAbbr(airportIcao, d);
  return abbr ? `${time} ${abbr}` : time;
}

export default timezones;
